import {
  Box,
  Button,
  Header,
  IconButton,
  Modal,
  Page,
  Splash,
  Table,
  GridLayout,
  Toggle,
  Tooltip,
  Icon,
  theme,
} from '@salecycle/ui-component-library';
import * as RestApi from 'core/network/RestApi';
import { RestApiError } from 'core/network/RestApi';
import useNotifications from 'core/notifications/useNotifications';
import Spinner from 'core/ui/components/spinner';
import humanizeDuration from 'humanize-duration';
import * as React from 'react';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { generatePath } from 'react-router';
import { useParams } from 'react-router-dom';
import { Column } from 'react-table';
import { SiteSettingsApiEndpoints } from '../../utils/api-endpoints';
import { IFrequencyCap } from '../entities/frequency-caps';
import { availableFrequencyCapFeatures, getAvailbleCapTypes, hasAvailableCap } from '../utils/frequencyCapUtils';
import FrequencyCapForm from './FrequencyCapForm';
import { useFrequencyCaps } from './hooks/useFrequencyCaps';
import { LegacyCard } from 'components/experimental/Card/LegacyCard';
import { useFeatureFlags } from 'core/app/AppContext';
import styled from 'styled-components';
export const TooltipWrapper = styled.div`
  text-align: left;
`;

const FrequencyCapsList: FC = props => {
  const { t } = useTranslation();
  const { showNotification } = useNotifications();
  const { siteId } = useParams<{ siteId: string }>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [frequencyCaps, setFrequencyCaps] = React.useState<IFrequencyCap[]>([]);
  const [hasMissingCapTypes, setHasMissingCapTypes] = React.useState<boolean>(true);
  const { isLoading, data, hasData, isSuccess } = useFrequencyCaps(siteId);
  const { flagEnabled } = useFeatureFlags();

  const deleteFrequencyCapsMutation = useMutation((frequencyCapId: string) =>
    RestApi.del(generatePath(SiteSettingsApiEndpoints.DELETE_FREQ_CAPS, { siteId, frequencyCapId })),
  );

  const handleFrequencyCapDelete = async (frequencyCapId: string) => {
    deleteFrequencyCapsMutation.mutate(frequencyCapId, {
      onSuccess: data => {
        showNotification(`Frequency cap deleted`, 'normal');
        const featureCapsWithoutDeleted = frequencyCaps.filter(cap => cap.id !== frequencyCapId);
        setFrequencyCaps([...featureCapsWithoutDeleted]);
      },
      onError: (error: RestApiError, variables, context) => {
        showNotification(`Unable to delete frequency cap`, 'error');
      },
    });
  };

  React.useEffect(() => {
    if (!isLoading && hasData) {
      setFrequencyCaps(data);
    }
  }, [isLoading, data, hasData]);

  React.useEffect(() => {
    setHasMissingCapTypes(hasAvailableCap(frequencyCaps, getAvailbleCapTypes()));
  }, [frequencyCaps]);

  const sortedFrequencyCaps = frequencyCaps.sort((a, b) => a.createdAt - b.createdAt);

  const tableColumns: Column[] = [
    {
      Header: t('settings.frequencyCaps.list.capTypeHeader'),
      // eslint-disable-next-line react/display-name
      Cell: tableProps => {
        return tableProps.original.type === 'abandonmentsPerPeriod' ? (
          <span style={{ flexGrow: 1 }}>
            <Tooltip
              html={
                <>
                  <p>{t(`settings.frequencyCaps.edit.types.abandonmentsPerPeriod.tooltipDescriptionP1`)}</p>
                  <p>{t(`settings.frequencyCaps.edit.types.abandonmentsPerPeriod.tooltipDescriptionP2`)}</p>
                </>
              }
              position="right"
            >
              <TooltipWrapper>
                {t(`settings.frequencyCaps.list.capTypes.${tableProps.original.type}`)}
                <Icon icon="question-circle" size="small" color={theme.vars.newColor.darkGS2} />
              </TooltipWrapper>
            </Tooltip>
          </span>
        ) : (
          <span style={{ flexGrow: 1 }}>{t(`settings.frequencyCaps.list.capTypes.${tableProps.original.type}`)}</span>
        );
      },
    },
    {
      Header: t('settings.frequencyCaps.list.descriptionHeader'),
      // eslint-disable-next-line react/display-name
      Cell: tableProps => {
        if (tableProps.original.type === 'purchaseCheck') {
          return (
            <span style={{ flexGrow: 1 }}>
              {t('settings.frequencyCaps.list.types.purchaseCheck.explanation')}
              <br></br>
              <b>{`${humanizeDuration(tableProps.original.period)}`}</b>
              {t('settings.frequencyCaps.list.types.purchaseCheck.reason')}
            </span>
          );
        } else {
          return (
            <span>
              {t('settings.frequencyCaps.list.types.abandonmentsPerPeriod.maximum')}
              <b>
                {`${tableProps.original.amount}`}
                {`${tableProps.original.amount === 1 ? ' abandonment event' : ' abandonment events'}`}
              </b>
              <br></br>
              {t('settings.frequencyCaps.list.types.abandonmentsPerPeriod.duration')}{' '}
              <b> {`${humanizeDuration(tableProps.original.period)}`}</b>
            </span>
            //
          );
        }
      },
    },
    {
      Header: t('settings.frequencyCaps.list.statusHeader'),
      // eslint-disable-next-line react/display-name
      Cell: () => {
        return (
          <Toggle
            label={t('settings.frequencyCaps.list.toggle.toggleOn')}
            ariaLabel={t('settings.frequencyCaps.list.toggle.toggleOn')}
            checked={frequencyCaps.length > 0}
          />
        );
      },
    },
    {
      // eslint-disable-next-line react/display-name
      Cell: tableProps => {
        const [deleteFrequencyCapModalActive, setDeleteFrequencyCapModal] = useState(false);
        const [editFrequencyCapModalActive, setEditFrequencyCapModal] = useState(false);
        const closeEditModal = () => {
          setEditFrequencyCapModal(false);
        };

        return (
          <Box horizontalPositioning="center">
            {flagEnabled('ldflag-freq-cap-edit') && (
              <IconButton
                onClick={() => setEditFrequencyCapModal(true)}
                ariaLabel={t(`settings.frequencyCaps.list.types.${tableProps.original.type}.editAriaLabel`)}
                icon="cog"
              />
            )}

            <IconButton
              onClick={() => setDeleteFrequencyCapModal(true)}
              ariaLabel={t(`settings.frequencyCaps.list.types.${tableProps.original.type}.deleteAriaLabel`)}
              icon="trash"
            />
            <Modal
              isOpen={editFrequencyCapModalActive}
              heading={t('settings.frequencyCaps.editFrequencyCap')}
              variation="clean"
            >
              <FrequencyCapForm
                heading={t('settings.frequencyCaps.addFrequencyCap')}
                siteId={siteId}
                closeModal={closeEditModal}
                setFrequencyCaps={setFrequencyCaps}
                savedFrequencyCaps={frequencyCaps}
                editFrequencyCap={editFrequencyCapModalActive}
                currentFrequencyCap={tableProps.original.type}
                currentFrequencyCapPeriod={tableProps.original.period}
                currentFrequencyCapAmount={tableProps.original.amount}
              />
            </Modal>
            <Modal
              isOpen={deleteFrequencyCapModalActive}
              onOkButtonClicked={() => handleFrequencyCapDelete(tableProps.original.id)}
              onCloseButtonClicked={() => setDeleteFrequencyCapModal(false)}
              heading={t('settings.frequencyCaps.list.delete.heading')}
              variation="clean"
              okButtonLabel={t('settings.frequencyCaps.list.delete.buttonLabel')}
              okButtonType="delete"
            >
              <p>{t('settings.frequencyCaps.list.delete.deleteDescription')}</p>
            </Modal>
          </Box>
        );
      },
      width: 175,
    },
  ];

  if (isLoading) {
    return <Spinner data-testid={'Spinner'} />;
  }

  const closeModal = () => {
    setIsModalOpen(false);
  };

  if (isSuccess) {
    return (
      <>
        <Page data-testid={'frequencyCapsList'}>
          {frequencyCaps.length > 0 ? (
            <>
              <Header
                heading={t('settings.frequencyCaps.list.heading')}
                description={t('settings.frequencyCaps.list.description')}
              />
              {availableFrequencyCapFeatures(frequencyCaps, t).length > 0 && (
                <Box margin="0 0 2rem 0">
                  <Tooltip
                    title={t('settings.frequencyCaps.list.addButton.tooltipTitle')}
                    html={t('settings.frequencyCaps.list.addButton.tooltipHtml')}
                  >
                    <Button variation="primary" onClick={() => setIsModalOpen(true)} disabled={!hasMissingCapTypes}>
                      {t('settings.frequencyCaps.addFrequencyCap')}
                    </Button>
                  </Tooltip>
                  <Modal
                    isOpen={isModalOpen}
                    heading={t('settings.frequencyCaps.addFrequencyCap')}
                    variation="clean"
                    //minHeight={500}
                    minWidth={500}
                  >
                    <FrequencyCapForm
                      heading={t('settings.frequencyCaps.addFrequencyCap')}
                      siteId={siteId}
                      closeModal={closeModal}
                      setFrequencyCaps={setFrequencyCaps}
                      savedFrequencyCaps={frequencyCaps}
                    />
                  </Modal>
                </Box>
              )}
              <Table data={sortedFrequencyCaps} columns={tableColumns} sortable={false} />
            </>
          ) : (
            <GridLayout childWidth={400} contentPadding={0}>
              <LegacyCard alignItems="center" justifyContent="center">
                <Box horizontalPositioning="center" stackItems={true}>
                  <Splash
                    imageUrl="/images/site-splash.svg"
                    heading={t('settings.frequencyCaps.noFrequencyCapsHeading')}
                    description={t('settings.frequencyCaps.list.description')}
                    imageHeight={200}
                  />
                  <Box horizontalPositioning="center">
                    <Button variation="primary" onClick={() => setIsModalOpen(true)}>
                      {t('settings.frequencyCaps.addFrequencyCap')}
                    </Button>
                  </Box>
                  <Modal
                    isOpen={isModalOpen}
                    heading={t('settings.frequencyCaps.addFrequencyCap')}
                    variation="clean"
                    //minHeight={400}
                    minWidth={500}
                  >
                    <FrequencyCapForm
                      heading={t('settings.frequencyCaps.addFrequencyCap')}
                      siteId={siteId}
                      closeModal={closeModal}
                      setFrequencyCaps={setFrequencyCaps}
                    />
                  </Modal>
                </Box>
              </LegacyCard>
            </GridLayout>
          )}
        </Page>
      </>
    );
  }

  return null;
};

export default FrequencyCapsList;
